// admin-flight-data.component.ts
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { FlightOption } from 'app/models/flights.model';
import { AdminFlightService } from 'app/services/admin-flight.service';

function toIsoDateOffset(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

function parseCityInput(value: string): string[] {
  return value
    .split(/\n|;/)
    .map(city => city.trim())
    .filter(Boolean);
}

@Component({
  selector: 'app-admin-flight-data',
  imports: [CommonModule, FormsModule, SvgLoaderComponent, SpinnerComponent],
  templateUrl: './admin-flight-data.component.html',
  styleUrls: ['./admin-flight-data.component.scss'],
  standalone: true,
})
export class AdminFlightDataComponent implements OnInit {
  flightOptions: FlightOption[] = [];
  seedFeedback: string = '';
  selectedFlightOption: FlightOption | null = null;
  showEditPopup: boolean = false;
  showSeedPopup: boolean = false;
  seedParams = {
    departureCities: ['Austin, TX, US', 'New York, NY, US', 'Los Angeles, CA, US'],
    arrivalCities: ['Miami, FL, US', 'Las Vegas, NV, US', 'Aspen, CO, US'],
    dateRangeStart: toIsoDateOffset(7),
    dateRangeEnd: toIsoDateOffset(21),
    numResults: 15
  };
  departureCitiesInput: string = this.seedParams.departureCities.join('\n');
  arrivalCitiesInput: string = this.seedParams.arrivalCities.join('\n');

  isLoadingFlightData: boolean = true;
  isGeneratingSeedData: boolean = false;
  loadError: string = '';

  @ViewChild('editPopup') editPopup!: ElementRef;
  @ViewChild('seedPopup') seedPopup!: ElementRef;

  constructor(private adminFlightService: AdminFlightService) {}

  ngOnInit() {
    this.loadTestFlightOptions();
  }

  loadTestFlightOptions() {
    this.isLoadingFlightData = true;
    this.loadError = '';
    this.adminFlightService.getTestFlightOptions().subscribe({
      next: options => {
        this.flightOptions = options;
        this.isLoadingFlightData = false;
      },
      error: error => {
        console.error('Error loading test flight options:', error);
        this.loadError = error?.message || 'Unable to load test flight options.';
        this.isLoadingFlightData = false;
      }
    });
  }

  editFlightOption(option: FlightOption) {
    this.selectedFlightOption = { ...option };
    this.showEditPopup = true;
  }

  saveFlightOption() {
    if (this.selectedFlightOption) {
      this.adminFlightService.updateFlightOption(this.selectedFlightOption).then(() => {
        this.loadTestFlightOptions();
        this.closeEditPopup();
      }).catch(error => {
        console.error('Error updating flight option:', error);
      });
    }
  }

  deleteFlightOption(id: string) {
    if (confirm('Are you sure you want to delete this test flight?')) {
      this.adminFlightService.deleteFlightOption(id).then(() => {
        this.loadTestFlightOptions();
      }).catch(error => {
        console.error('Error deleting flight option:', error);
      });
    }
  }

  openSeedPopup() {
    this.departureCitiesInput = this.seedParams.departureCities.join('\n');
    this.arrivalCitiesInput = this.seedParams.arrivalCities.join('\n');
    this.showSeedPopup = true;
  }

  saveSeedData(form: NgForm) {
    this.isGeneratingSeedData = true;
    this.seedParams.departureCities = parseCityInput(this.departureCitiesInput);
    this.seedParams.arrivalCities = parseCityInput(this.arrivalCitiesInput);

    if (this.seedParams.departureCities.length === 0 || this.seedParams.arrivalCities.length === 0) {
      this.seedFeedback = 'Add at least one departure city and one arrival city.';
      this.isGeneratingSeedData = false;
      return;
    }

    this.adminFlightService.seedTestFlightOptions({
      departureCities: this.seedParams.departureCities,
      arrivalCities: this.seedParams.arrivalCities,
      dateRangeStart: this.seedParams.dateRangeStart,
      dateRangeEnd: this.seedParams.dateRangeEnd,
      numResults: this.seedParams.numResults
    }).then(response => {
      this.seedFeedback = `Successfully added ${response.numAdded} test flight options!`;
      this.loadTestFlightOptions();
      this.closeSeedPopup();
      this.isGeneratingSeedData = false;
    }).catch(error => {
      this.seedFeedback = `Error seeding data: ${error.message}`;
      this.isGeneratingSeedData = false;
    });
  }

  refreshTestDataTiming() {
    this.seedFeedback = '';
    this.adminFlightService.refreshTestDataTiming().then(() => {
      this.seedFeedback = 'Test flight dates refreshed!';
      this.loadTestFlightOptions();
    }).catch(error => {
      this.seedFeedback = `Error refreshing dates: ${error.message}`;
    });
  }

  closeEditPopup() {
    this.showEditPopup = false;
    this.selectedFlightOption = null;
  }

  closeSeedPopup() {
    this.showSeedPopup = false;
  }
}
